import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { getAccumulativeData, getNormalizationData, getTimeSeriesData, getWidgetData } from "api/report";

import { getReportDurationRangesUnix, getReportPreviewEnd, getReportPreviewStart } from "utils/reportUtils";
import { getFiltersPayload } from "utils/utils";
import { getGraphDataPointsInterval } from "utils/utils";
import parseWidgetData from "utils/widgetUtils";
import { valueStrings } from "constants/constantStrings";
import { WIDGET_TYPE_GROUP_INTERVAL, WIDGET_TYPES } from "constants/widgets.type.const";

import ErrorLabel from "components/ErrorLabel";
import PageLoading from "components/PageLoading";
import { previewLayout } from "components/Reports/ReportConfig";
import ReportWidgetModel from "components/Reports/ReportWidgetModel";

const ReportChart = ({ report, parentStyle, error, setError, isToUpdateReportPreview }) => {
 const [data, setData] = useState(report.data);
  const [width, setWidth] = useState(300);
  const ref = useRef(null);
  const tagsAsObj = useSelector(state => state.tags.tagsAsObj);
  const [isLoading, setLoading] = useState(report.isToUpdatePreview);

  const formatTagValue = () => {
    let tagVal = "";
    if (report.tag !== null && report.tag !== undefined && report.unit) {
      tagVal = `${report.tag} ${report.unit}`;
    } else if (report.tag !== null && report.tag !== undefined) {
      tagVal = report.tag;
    }
    return tagVal;
  };
  const [tagValue, setTagValue] = useState(formatTagValue());
  
  const isToShowChartPreview =
    report.Type === "Normalization" ||
    report.Type === "Timeseries" ||
    report.Type === "Prediction" ||
    report.Type === "BarChart" ||
    report.Type === "Gauge";

  const resize = () => {
    if (ref.current && ref.current.offsetWidth) {
      setWidth(ref.current.offsetWidth);
    }
  };

  useEffect(() => {
    if (report.Type === WIDGET_TYPES.TAG) {
      if (tagValue) {
        report.tag = tagValue;
        report.error = null;
      } else {
        report.error = error ? error : valueStrings.noData;
      }
    }
  }, [tagValue, error]);

  useEffect(() => {
    window.addEventListener("resize", resize());
    return () => {
      window.removeEventListener("resize", resize());
    };
  });

  useEffect(() => {
    if (ref.current && ref.current.offsetWidth) {
      setWidth(ref.current.offsetWidth);
    }
  }, [ref.current]);

  const resetValue = () => {
    setError(null);
    setData(null);
    report.data = null;
    report.tag = "";
    report.error = null;
  };

  useEffect(() => {
    if (report.isToUpdatePreview) {
      const fetchData = async () => {
        resetValue();
        let response = null;
        const data = parseWidgetData(report, tagsAsObj);
        let tagId = Object.fromEntries(Object.entries(report.Attributes).filter(([key]) => key.includes("SystemTag")));
        report.tagId = tagId;
        tagId = Object.values(tagId);
        let params = getReportDurationRangesUnix();
        const group = getGraphDataPointsInterval(params);
        try {
          if (report.Type === WIDGET_TYPES.TIMESERIES || report.Type === WIDGET_TYPES.TAG || report.Type === WIDGET_TYPES.BAR_CHART) {
            params = { ...params, calculation: report.Attributes.Function, tags: tagId, group };
            if (report.Type === WIDGET_TYPES.TAG) {
              params = { ...params, group: WIDGET_TYPE_GROUP_INTERVAL.TAG };
            }
            if (tagId[0][0]?.includes("CUSTOM_")) {
              const tags = tagId[0][0]?.split("CUSTOM_")[1];
              const payload = { startDate: params.startTime, endDate: params.endTime, tags };
              response = await getAccumulativeData(payload);
              response.data["CUSTOM_" + tags] = response.data[tags];
              delete response.data[tags];
            } else {
              response = await getTimeSeriesData(params);
            }
          } else if (report.Type === WIDGET_TYPES.PREDICTION || report.Type === WIDGET_TYPES.GAUGE) {
            response = await getWidgetData(params, report.Id);
          } else if (report.Type === WIDGET_TYPES.NORMALIZATION) {
            const normalizedFiltersParms = getFiltersPayload(report.Attributes);
            params = { ...params, tags: tagId, baselineId: report.Attributes.Baseline, ...normalizedFiltersParms, group };
            response = await getNormalizationData(params, report.Id);
          }
          if (response && Object.keys(response.data)?.length !== 0) {
            data.Data = response.data;
            setTagValues(data.Data, tagId);
          } else if (response === "undefined") {
            report.error = valueStrings.noData;
            setError(valueStrings.noData);
          }
          report.data = data;
          setData(data);
        } catch (error) {
          report.error = valueStrings.noData;
          setError(valueStrings.noData);
        } finally {
          report.isToUpdatePreview = false;
          setLoading(false);
        }
      };
      fetchData();
      setLoading(true);
    }
  }, [isToUpdateReportPreview, report.isToUpdatePreview]);

  const setTagValues = (data, tagId) => {
    setTagValue(null);
    if (report.Type === WIDGET_TYPES.TAG) {
      setLoading(true);
      const tagValue = data[tagId][0];
      const tagUnit = tagsAsObj[tagId]?.Units;
      report.tag = tagValue !== null && tagValue !== undefined ? tagValue : "";
      report.unit = tagUnit;
      if (tagValue === null || tagValue === undefined) {
        report.error = valueStrings.noData;
        setError(valueStrings.noData);
      } else {
        setTagValue(formatTagValue());
      }
      setLoading(false);
    }
  };

  return (
    <div ref={ref} className={`${parentStyle}  width100 chartContainer ${error ? null : "align-self-center"} `}>
      {error && report.Type !== WIDGET_TYPES.GAUGE ? (
        <ErrorLabel message={error} className="position-relative  text-center" />
      ) : (
        isToShowChartPreview && (
          <div className="d-flex flex-center-center align-self-center width100">
            <div className="align-self-center  width100">
              <ReportWidgetModel
                report={report}
                startTime={getReportPreviewStart()}
                endTime={getReportPreviewEnd()}
                data={data}
                setData={setData}
                intialLayout={previewLayout}
                error={error}
                setError={setError}
                isLoading={isLoading}
                setLoading={setLoading}
                width={width}
              />
            </div>
          </div>
        )
      )}
      <div className="flex-center-center">
        {isLoading && report.Type === WIDGET_TYPES.TAG ? (
          <PageLoading customHeight="200px" />
        ) : (
          report.Type === WIDGET_TYPES.TAG && !error && tagValue && <div className="calculatedValueText">{tagValue}</div>
        )}
        {report.Type === WIDGET_TYPES.FREE_TEXT && report.Attributes.Text}
      </div>
    </div>
  );
};

export default ReportChart;
